import type { ReactNode } from "react";

import { Chip } from "ui/chip";
import { Box } from "ui/box";
import { Spinner } from "ui/spinner";
import { Stack } from "ui/stack";
import { Table, TableBody, TableCell, TableHead, TableRow } from "ui/table";
import { Typography } from "ui/typography";
import { TruncatedText } from "ui/truncated-text";

import { ChannelIcon } from "components/partials/channel-icon/channel-icon";

import { EMPTY_DISPLAY_VALUE } from "constants/ui";

import type {
  CreativePopulationType,
  CreativeRow,
  SummaryField,
} from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary.utils";

const POPULATION_TYPE_LABEL: Record<CreativePopulationType, string> = {
  student: "Student",
  parent: "Parent",
};

// The theme has no 18px/600 slot — `h4` is 18/700 and `subtitle1` is 16/600 — so these headings
// state every metric instead of overriding a variant.
export const SECTION_HEADING_SX = {
  fontSize: "1.125rem",
  lineHeight: "1.75rem",
  fontWeight: 600,
  color: "grey.900",
} as const;

const TABLE_CONTAINER_SX = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  overflow: "hidden",
  backgroundColor: "common.white",
} as const;

const TABLE_HEADER_CELL_SX = {
  backgroundColor: "grey.100",
  letterSpacing: "0.25px",
} as const;

const TABLE_BODY_CELL_SX = { color: "grey.600" } as const;

const TABLE_TRUNCATED_CELL_SX = { ...TABLE_BODY_CELL_SX, maxWidth: 240 } as const;

const hasCreativeCellContent = (value: string) => value.trim().length > 0 && value !== EMPTY_DISPLAY_VALUE;

type CreativeTableColumn = {
  id: string;
  label: string;
  cellSx: typeof TABLE_BODY_CELL_SX | typeof TABLE_TRUNCATED_CELL_SX;
  hasContent: (row: CreativeRow) => boolean;
  renderCell: (row: CreativeRow) => ReactNode;
};

const CREATIVE_TABLE_COLUMNS: CreativeTableColumn[] = [
  {
    id: "channel",
    label: "Channel",
    cellSx: TABLE_BODY_CELL_SX,
    hasContent: (row) => hasCreativeCellContent(row.channel),
    renderCell: (row) => (
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <ChannelIcon channel={row.channelValue} sx={{ color: "grey.900" }} />
        <Typography component="span" variant="inherit">
          {row.channel}
        </Typography>
      </Stack>
    ),
  },
  {
    id: "populationType",
    label: "Population type",
    cellSx: TABLE_BODY_CELL_SX,
    hasContent: () => true,
    renderCell: (row) => (
      <Chip size="small" variant="outlined" label={POPULATION_TYPE_LABEL[row.populationType]} />
    ),
  },
  {
    id: "quantity",
    label: "Quantity",
    cellSx: TABLE_BODY_CELL_SX,
    hasContent: (row) => hasCreativeCellContent(row.quantity),
    renderCell: (row) => row.quantity,
  },
  {
    id: "overview",
    label: "Overview",
    cellSx: TABLE_TRUNCATED_CELL_SX,
    hasContent: (row) => hasCreativeCellContent(row.overview),
    renderCell: (row) => <TruncatedText text={row.overview} />,
  },
  {
    id: "additionalDetails",
    label: "Additional details",
    cellSx: TABLE_TRUNCATED_CELL_SX,
    hasContent: (row) => hasCreativeCellContent(row.additionalDetails),
    renderCell: (row) => <TruncatedText text={row.additionalDetails} />,
  },
];

type SummaryFieldListProps = {
  title: string;
  fields: SummaryField[];
};

const SummaryFieldList = ({ title, fields }: SummaryFieldListProps) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
      <Typography component="h4" sx={SECTION_HEADING_SX}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {fields.map((field) => (
          <Box key={field.label}>
            <Typography variant="body2" sx={{ color: "grey.500" }}>
              {field.label}
            </Typography>
            <Typography sx={{ color: "grey.900", fontWeight: 500 }}>{field.value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type CampaignCreativesTableProps = {
  rows: CreativeRow[];
};

const CampaignCreativesTable = ({ rows }: CampaignCreativesTableProps) => {
  const columns = CREATIVE_TABLE_COLUMNS.filter((column) => rows.some((row) => column.hasContent(row)));

  if (rows.length === 0 || columns.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "grey.500", textAlign: "center", py: 3 }}>
        No creatives added yet.
      </Typography>
    );
  }

  return (
    <Box sx={TABLE_CONTAINER_SX}>
      <Box sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} sx={TABLE_HEADER_CELL_SX}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                {columns.map((column) => (
                  <TableCell key={column.id} sx={column.cellSx}>
                    {column.renderCell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

type CampaignTemplateSummaryContentProps = {
  isLoading: boolean;
  generalFields: SummaryField[];
  logicFields: SummaryField[];
  creativeRows: CreativeRow[];
  fieldsLayout?: "row" | "column";
};

export const CampaignTemplateSummaryContent = ({
  isLoading,
  generalFields,
  logicFields,
  creativeRows,
  fieldsLayout = "row",
}: CampaignTemplateSummaryContentProps) =>
  isLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flex: 1,
      }}>
      <Spinner />
    </Box>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: fieldsLayout, gap: fieldsLayout === "row" ? 7 : 3 }}>
        <SummaryFieldList title="General" fields={generalFields} />
        <SummaryFieldList title="Campaign logic" fields={logicFields} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Typography component="h4" sx={SECTION_HEADING_SX}>
          Campaign creatives
        </Typography>
        <CampaignCreativesTable rows={creativeRows} />
      </Box>
    </Box>
  );
